/**
 * HTMX integration for Shadow DOM.
 *
 * Wires up the event listeners that keep HTMX, Alpine, and Bootstrap
 * in sync whenever content is swapped inside the shadow boundary.
 */

import { destroyTree, initTree } from './alpine-integration.js';
import { initializeAll } from './bootstrap-components.js';
import { updatePageCss } from './css-loader.js';

/**
 * Register all HTMX-related event listeners on the shadow root.
 *
 * @param {ShadowRoot} shadowRoot
 * @param {HTMLElement} wrapper - the #htmx-content-wrapper element
 */
export function registerHtmxListeners(shadowRoot, wrapper) {
    // --- Before swap: resolve the target in the shadow root, tear down Alpine ---
    shadowRoot.addEventListener('htmx:beforeSwap', (event) => {
        // A server-side HX-Retarget header wins over the element's hx-target
        // attribute. Resolve it here: htmx resolves selectors against the
        // document and cannot see elements inside the shadow root.
        const retarget = event.detail.xhr?.getResponseHeader('HX-Retarget');
        const retargetEl = retarget && retarget !== 'this'
            ? shadowRoot.querySelector(retarget)
            : null;

        if (retargetEl) {
            event.detail.target = retargetEl;
        } else {
            const triggerElt = event.detail.requestConfig?.elt;
            if (triggerElt && triggerElt.hasAttribute('hx-target')) {
                const selector = triggerElt.getAttribute('hx-target');
                const newTarget = selector === 'this'
                    ? triggerElt
                    : shadowRoot.querySelector(selector);
                if (newTarget) {
                    event.detail.target = newTarget;
                }
            }
        }

        if (event.detail.target) destroyTree(event.detail.target);
    });


    shadowRoot.addEventListener('htmx:configRequest', (event) => {
        const target = event.detail.elt;
        if (target?.hasAttribute('hx-target')) {
            event.detail.headers['HX-Target'] = target.getAttribute('hx-target');
        }

        // Forward Spring Security CSRF token as request header.
        // Primary: read from XSRF-TOKEN cookie (CookieCsrfTokenRepository.withHttpOnlyFalse()).
        // Fallback: read from Thymeleaf-rendered meta tags in shadow DOM.
        const csrfCookie = document.cookie
            .split('; ')
            .find(c => c.startsWith('XSRF-TOKEN='))
            ?.split('=')[1];
        if (csrfCookie) {
            event.detail.headers['X-XSRF-TOKEN'] = decodeURIComponent(csrfCookie);
        } else {
            const tokenMeta = shadowRoot.querySelector('meta[name="_csrf"]');
            const headerMeta = shadowRoot.querySelector('meta[name="_csrf_header"]');
            const token = tokenMeta?.getAttribute('content');
            const headerName = headerMeta?.getAttribute('content') || 'X-CSRF-TOKEN';
            if (token) event.detail.headers[headerName] = token;
        }
    });

    // Native form fallback: any plain <form method="post"> inside the shadow root
    // gets a hidden _csrf input auto-injected from the meta tag before submit.
    shadowRoot.addEventListener('submit', (evt) => {
        const form = evt.target;
        if (!form || form.tagName !== 'FORM') return;
        if ((form.method || '').toLowerCase() !== 'post') return;
        if (form.querySelector('input[name="_csrf"]')) return;
        const meta = shadowRoot.querySelector('meta[name="_csrf"]')
            || document.querySelector('meta[name="_csrf"]');
        if (!meta) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = '_csrf';
        input.value = meta.getAttribute('content');
        form.appendChild(input);
    }, true);

    // --- After swap: init env variable, re-init Alpine, HTMX, and Bootstrap ---------------------
    wrapper.addEventListener('htmx:afterSwap', async (evt) => {
        const target = evt.detail.target;

        // The swap target may have been replaced ? look it up again by id
        let element = target;
        if (!shadowRoot.contains(target) && target.id) {
            element = shadowRoot.getElementById(target.id);
        }

        if (!element || !shadowRoot.contains(element)) return;

        // For beforebegin swaps, the new content is the previous sibling
        let initTarget = element;
        if (evt.detail.requestConfig?.elt?.getAttribute('hx-swap') === 'beforebegin') {
            initTarget = element.previousElementSibling;
        }

        initTree(shadowRoot, initTarget);

        // Let HTMX discover new hx-* attributes in the injected HTML
        if (window.htmx) {
            window.htmx.process(target);
        }

        // replace title of page
        const pageTitle = shadowRoot.getElementById('page-title');
        if (pageTitle) {
            document.querySelector('title').innerHTML = pageTitle.dataset.title;
        }

        // load page-specific CSS declared via <template id="css-links" th:attr="data-path1=@{...}">
        const cssLinks = shadowRoot.getElementById('css-links');
        const paths = [];
        if (cssLinks) {
            let i = 1;
            while (cssLinks.dataset['path' + i]) {
                paths.push(cssLinks.dataset['path' + i]);
                i++;
            }
        }
        await updatePageCss(shadowRoot, paths);

        // Re-bind
        initializeAll(shadowRoot);
    });

    shadowRoot.addEventListener('htmx:beforeHistorySave', function() {
        document.querySelectorAll('body script').forEach(s => s.remove());
    });

// Override popstate entirely for pages with next-htmx
    window.addEventListener('popstate', function(evt) {
        const container = document.getElementById('next-htmx');
        if (!container) return;

        // Let the browser handle the URL, force a fresh page load
        window.location.reload();
    }, true); // capture phase = fires before htmx's listener
}

/**
 * Replace the wrapper's content and re-process it (imperative API).
 * Also handles page title and page-specific CSS (same logic as htmx:afterSwap,
 * needed when the extension intercepts the swap and calls this directly).
 *
 * @param {ShadowRoot} shadowRoot
 * @param {string}     html
 */
export async function setContent(shadowRoot, html) {
    const container = shadowRoot.getElementById('htmx-content-wrapper');
    if (!container) return;

    destroyTree(container);
    container.innerHTML = html;

    if (window.htmx) {
        Array.from(container.children).forEach(child => window.htmx.process(child));
    }

    if (window.Alpine) {
        Array.from(container.children).forEach(child => window.Alpine.initTree(child));
    }

    // replace title of page
    const pageTitle = shadowRoot.getElementById('page-title');
    if (pageTitle) {
        document.querySelector('title').innerHTML = pageTitle.dataset.title;
    }

    // load page-specific CSS declared via <template id="css-links" th:attr="data-path1=@{...}">
    const cssLinks = shadowRoot.getElementById('css-links');
    const paths = [];
    if (cssLinks) {
        let i = 1;
        while (cssLinks.dataset['path' + i]) {
            paths.push(cssLinks.dataset['path' + i]);
            i++;
        }
    }
    await updatePageCss(shadowRoot, paths);

    initializeAll(shadowRoot);
}